import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { getAllProducts } from 'indices-list'
import moment from 'moment'
import { ProductOverview } from 'pages/overview/storage/interface'
import { createPrices } from 'services/price-history'
import { assetConfig, dailySeconds } from 'services/price-history/config'
import { Portfolios } from 'services/price-history/interfaces'
import {
  getPriceRangesStore,
  getPricesStore,
  writePriceRangesStore,
  writePricesStore,
} from 'services/price-history/utils'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_ALL_PRODUCT_INFO } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { getSubgraphNewUrl } from 'services/query/hooks'
import { assertMultiply } from 'utils'
import { noStakingProduct } from 'utils/product-service'
import { Address } from 'viem'
import { z } from 'zod'
import { historicalAssetsStatsQuery } from '../index/daily-statistics'
import { DailyStatistics } from '../index/daily-statistics/model'
import { getProductPortfolio, parseStorageToPortfolios } from '../index/daily-statistics/utils'
import { getParamsForIndexInfoData } from '../index/product-info/query-params'
import { generateProductOverview, getProductInfoFetcher } from './query-params'

const ResponseSchema = z.object({
  address: z.custom<Address>(),
  chainId: z.custom<NetworkType>(),
  name: z.string(),
  symbol: z.string(),
  basePrice: z.custom<Big>(),
  basePriceETH: z.custom<Big>(),
  decimals: z.number(),
  marketCap: z.custom<Big>(),
  totalSupply: z.custom<Big>(),
  feeBurn: z.number(),
  feeMint: z.number(),
  feeAUMPercent: z.number(),
  created: z.date(),
  assets: z
    .array(
      z.object({
        asset: z.string(),
        balance: z.string(),
        chainID: z.string(),
        currencyID: z.string().nullable(),
        decimals: z.number(),
        name: z.string(),
        symbol: z.string(),
        weight: z.string().nullable(),
      }),
    )
    .optional(),
})

export const allProductDataQuery = async <T>({
  queryKey: [key],
}: {
  queryKey: [string]
}): Promise<T[]> => {
  const products = getAllProducts()
  const now = moment().unix()
  return await Promise.all(
    products.filter(noStakingProduct).map(async (product) => {
      const { address, productType, network } = product
      const productAddress = address as string
      const productKey = `${network as number}:${address}`
      let productPrices: {
        [timestamp: number]: string
      } = {}
      const rawPortfolio = getProductPortfolio(productKey)
      let portfolio: Portfolios[] = []
      if (!rawPortfolio || now - rawPortfolio.lastUpdate >= 60) {
        portfolio = await historicalAssetsStatsQuery(
          key,
          network,
          product.address,
          'allProductDataQuery',
        )
      } else {
        portfolio = parseStorageToPortfolios(rawPortfolio)
      }
      const { isError } = await createPrices(
        assetConfig,
        portfolio,
        getPricesStore,
        getPriceRangesStore,
        writePricesStore,
        writePriceRangesStore,
      )
      const currentPrices = await getPricesStore()
      productPrices = currentPrices[productKey]

      const url = getSubgraphNewUrl(productAddress)
      // get the daily stats for this product

      // get the product data for this product
      const productFetcher = getProductInfoFetcher(productType, network, key, 'allProductDataQuery')
      const res = await productFetcher(url, getParamsForIndexInfoData(productAddress))
      if (!res) {
        throw new Error(`Fails in allProductDataQuery function with a key ${key}`)
      }

      const response = ResponseSchema.parse(res)

      const productPortfolio = getProductPortfolio(productKey)

      const daily = Object.entries(productPrices ?? {})
      const priceHistory = daily.map((dayData) => {
        const [day, price] = dayData

        assertMultiply(dailySeconds, day)
        const timestamp = Big(dailySeconds).mul(day)
        const intersection = productPortfolio?.history.find((history) =>
          timestamp.eq(history.timestamp),
        )
        assertMultiply(timestamp, 1000)
        return {
          basePrice: Big(price),
          basePriceETH: Big(0),
          totalSupply: Big(intersection?.totalSupply ?? 0),
          date: new Date(+timestamp.mul(1000).toString()),
        }
      })
      const productValues = Object.values(productPrices ?? {})
      const dailyPrices: DailyStatistics = {
        chainId: network,
        productAddress,
        currentPrice: Big(productValues[productValues.length - 1]),
        priceHistory,
        isError,
      }

      return generateProductOverview(response, dailyPrices) as T
    }),
  )
}

// Return all index request
export const allProductsDataFactory = () => {
  const mainProperties = createAtomsWithQuery<ProductOverview[], null, null, null, [string]>(
    {},
    QUERY_KEY_ALL_PRODUCT_INFO,
    allProductDataQuery<ProductOverview>,
    DEFAULT_REFETCH_INTERVAL,
    'allProductDataQuery',
  )

  return mainProperties
}
